import { fetchPage, deletePage, postPage } from "@/services/config";
import { useAuthStore } from "@/stores";
import { createHttpError } from "@core/http";
import { currentDefaultVersion } from "@/assets/mobiusVersioningData";
import type {
  ApiTenant,
  ChargebeeSubscription,
  ListTenantsParams,
  ListTenantsResponse,
  TenantSignupPayload,
  TenantUser,
  TenantWithRedirect,
  UpdateTenantItem
} from "@/types";

const serviceBase = "tenant-management";

// Admin-facing methods -----------

export function listTenants(params: ListTenantsParams = {}): Promise<ListTenantsResponse> {
  return fetchPage<ListTenantsResponse>(serviceBase, "/tenant", { ...params });
}

export function updateTenants(data: UpdateTenantItem[]): Promise<string[]> {
  return postPage<string[], UpdateTenantItem[]>(serviceBase, "/tenant", data, {}, "PATCH");
}

export function getTenantById(tenantId: number): Promise<ApiTenant> {
  return fetchPage<ApiTenant>(serviceBase, `/tenant/${tenantId}`);
}

export function updateTenant(tenantId: number, data: Partial<ApiTenant>): Promise<ApiTenant> {
  return postPage<ApiTenant, Partial<ApiTenant>>(serviceBase, `/tenant/${tenantId}`, data, {}, "PATCH");
}

export async function deleteTenant(tenantId: number): Promise<void> {
  await deletePage(serviceBase, `/tenant/${tenantId}`);
}

export function getTenantUsersAsAdmin(tenantId: number): Promise<TenantUser[]> {
  return fetchPage<TenantUser[]>(serviceBase, `/tenant/${tenantId}/user`);
}

export function createTenantUserAsAdmin(tenantId: number, userData: Omit<TenantUser, "id">): Promise<TenantUser> {
  return postPage<TenantUser, Omit<TenantUser, "id">>(serviceBase, `/tenant/${tenantId}/user`, userData);
}

export async function deleteTenantUserAsAdmin(tenantId: number, username: string): Promise<void> {
  await deletePage(serviceBase, `/tenant/${tenantId}/user/${username}`);
}

export async function grantAccessToTenant(tenantId: number): Promise<void> {
  await postPage(serviceBase, `/admin/grant_access/${tenantId}`);
}

export async function removeAccessToTenant(tenantId: number): Promise<void> {
  await postPage(serviceBase, `/admin/remove_access/${tenantId}`);
}

// Admin user management -----------

export function listAdminUsers(): Promise<TenantUser[]> {
  return fetchPage<TenantUser[]>(serviceBase, "/admin");
}

export function createAdminUser(userData: TenantUser): Promise<TenantUser> {
  return postPage<TenantUser, TenantUser>(serviceBase, "/admin", userData);
}

export async function deleteAdminUser(username: string): Promise<void> {
  await deletePage(serviceBase, `/admin/${username}`);
}

// Regular user-facing methods -----------

export function getSubscriptionForTenant(tenantId: number): Promise<ChargebeeSubscription> {
  return fetchPage<ChargebeeSubscription>(serviceBase, `/account/${tenantId}/subscription`);
}

// TODO for backend: since this is for handling plan only, we shouldn't have to include any other tenant data in the payload.
export function handlePlan(tenantId: number, tenantName: string, planId: string): Promise<TenantWithRedirect> {
  const apiEndpoint = `/account/${tenantId}/handle_plan?redirect_url=${window.location.origin}/billing`;
  const payload = { tenant_name: tenantName, version_tag: currentDefaultVersion, plan_id: planId };
  return postPage<TenantWithRedirect, typeof payload>(serviceBase, apiEndpoint, payload);
}

export async function cancelSubscription(tenantId: number): Promise<void> {
  await postPage(serviceBase, `/account/${tenantId}/subscription_cancel`);
}

export async function reactivateSubscription(tenantId: number): Promise<void> {
  await postPage(serviceBase, `/account/${tenantId}/subscription_reactivate`);
}

export function managePaymentSources(tenantId: number): Promise<TenantWithRedirect> {
  const apiEndpoint = `/account/${tenantId}/manage_payment_sources?redirect_url=${window.location.origin}/billing`;
  return postPage<TenantWithRedirect>(serviceBase, apiEndpoint);
}

export function tenantSignup(payload: TenantSignupPayload): Promise<TenantWithRedirect> {
  const apiEndpoint = `/account/signup?redirect_url=${window.location.origin}`;
  return postPage<TenantWithRedirect, TenantSignupPayload>(serviceBase, apiEndpoint, payload);
}

export function getTenantUsersAsTenantUser(tenantId: number): Promise<TenantUser[]> {
  return fetchPage<TenantUser[]>(serviceBase, `/account/${tenantId}/user`);
}

export function createTenantUserAsTenantUser(tenantId: number, userData: Omit<TenantUser, "id">): Promise<TenantUser> {
  return postPage<TenantUser, Omit<TenantUser, "id">>(serviceBase, `/account/${tenantId}/user`, userData);
}

export async function deleteTenantUserAsTenantUser(tenantId: number, username: string): Promise<void> {
  await deletePage(serviceBase, `/account/${tenantId}/user/${username}`);
}

export async function exportAllTenantUsers(filename = "tenant-users.csv"): Promise<void> {
  const { authFetch } = useAuthStore();

  const fullUrl = `/api/${serviceBase}/tenant/export/users`;
  const response = await authFetch(fullUrl, { method: "GET" });

  if (!response.ok) throw await createHttpError(response, fullUrl);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    // Offload URL revocation to event loop; safer than immediate revoke in some browsers
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
